import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatRomeDate } from '../lib/datetime';
import { api, useAuth } from '../contexts/AuthContext';
import LatestPatchNotesCard from '../components/LatestPatchNotesCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import AreaCombobox from '../components/AreaCombobox';
import { toast } from 'sonner';
import {
  Plus,
  Ticket,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Building2,
  Loader2,
  ChevronRight,
  RefreshCw,
  Send
} from 'lucide-react';

const priorityLabels = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Media',
  bassa: 'Bassa'
};

const statusLabels = {
  aperto: 'Aperto',
  assegnato: 'Assegnato',
  in_lavorazione: 'In Lavorazione',
  in_attesa_pezzi: 'In Attesa Pezzi',
  completato: 'Completato',
  chiuso: 'Chiuso'
};

const FrontofficeDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    hotel_id: '',
    area: '',
    category_id: '',
    priority: 'media',
    is_room_occupied: false,
    operator_signature: ''
  });
  const [hotelAreas, setHotelAreas] = useState({ rooms: [], common_areas: [], all_areas: [] });

  const fetchHotelAreas = async (hotelId) => {
    if (!hotelId) {
      setHotelAreas({ rooms: [], common_areas: [], all_areas: [] });
      return;
    }
    try {
      const { data: ar } = await api.get(`/hotels/${hotelId}/areas`);
      setHotelAreas(ar);
    } catch {
      setHotelAreas({ rooms: [], common_areas: [], all_areas: [] });
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, catsRes] = await Promise.all([
        api.get('/dashboard/frontoffice'),
        api.get('/categories')
      ]);
      setData(dashboardRes.data);
      setCategories(catsRes.data);

      // Auto-select first hotel and prefer "Generale" category (or first available) by default
      const firstHotelId = dashboardRes.data.hotels?.[0]?.id;
      const defaultCat =
        catsRes.data.find(c => /general/i.test(c.name))?.id || catsRes.data[0]?.id || '';
      if (firstHotelId) {
        setNewTicket(prev => ({
          ...prev,
          hotel_id: prev.hotel_id || firstHotelId,
          category_id: prev.category_id || defaultCat,
        }));
        if (firstHotelId) fetchHotelAreas(firstHotelId);
      } else if (defaultCat) {
        setNewTicket(prev => ({ ...prev, category_id: prev.category_id || defaultCat }));
      }
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (submitting) return; // Guard contro doppio-click / rapid fire submit
    if (!newTicket.title || !newTicket.hotel_id || !newTicket.area || !newTicket.category_id) {
      toast.error('Compila tutti i campi obbligatori (titolo, hotel, camera/area, categoria)');
      return;
    }
    const sig = (newTicket.operator_signature || '').trim();
    if (sig.length < 2) {
      toast.error('Firma operatore obbligatoria (minimo 2 caratteri)');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/tickets', { ...newTicket, operator_signature: sig });
      toast.success('Ticket creato con successo!');
      setCreateOpen(false);
      setNewTicket({
        title: '',
        description: '',
        hotel_id: data?.hotels?.[0]?.id || '',
        area: '',
        category_id: categories.find(c => /general/i.test(c.name))?.id || categories[0]?.id || '',
        priority: 'media',
        is_room_occupied: false,
        operator_signature: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione del ticket');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="frontoffice-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="frontoffice-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Front Office</h1>
          <p className="text-slate-500 mt-1">Benvenuto/a, {user?.name}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </Button>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 gap-2" data-testid="create-ticket-button">
                <Plus className="w-4 h-4" />
                Apri Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Apri Nuovo Ticket</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTicket} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Titolo del Problema *</Label>
                  <Input
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                    placeholder="Descrivi brevemente il problema"
                    required
                    data-testid="ticket-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Descrizione Dettagliata *</Label>
                  <Textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    placeholder="Fornisci tutti i dettagli utili per la risoluzione..."
                    rows={4}
                    required
                    data-testid="ticket-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hotel *</Label>
                    {(data?.hotels?.length || 0) <= 1 ? (
                      <div
                        className="flex h-9 items-center rounded-md border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                        data-testid="ticket-hotel-readonly"
                      >
                        {data?.hotels?.[0]?.name || 'Nessun hotel assegnato'}
                      </div>
                    ) : (
                      <Select
                        value={newTicket.hotel_id}
                        onValueChange={(v) => {
                          setNewTicket({...newTicket, hotel_id: v, area: ''});
                          fetchHotelAreas(v);
                        }}
                      >
                        <SelectTrigger data-testid="ticket-hotel-select">
                          <SelectValue placeholder="Seleziona hotel" />
                        </SelectTrigger>
                        <SelectContent>
                          {data?.hotels?.map(h => (
                            <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Camera/Area *</Label>
                    <AreaCombobox
                      value={newTicket.area}
                      onChange={(v) => setNewTicket({...newTicket, area: v})}
                      rooms={hotelAreas.rooms || []}
                      commonAreas={hotelAreas.common_areas || []}
                      disabled={!newTicket.hotel_id}
                      placeholder={
                        !newTicket.hotel_id
                          ? "Seleziona prima l'hotel"
                          : (hotelAreas.all_areas?.length > 0
                              ? 'Scegli o digita...'
                              : 'es. 301, Hall, Reception')
                      }
                      testId="fo-area-combobox"
                      inputTestId="ticket-area-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <Select
                      value={newTicket.category_id}
                      onValueChange={(v) => setNewTicket({...newTicket, category_id: v})}
                    >
                      <SelectTrigger data-testid="ticket-category-select">
                        <SelectValue placeholder="Seleziona categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Priorità</Label>
                    <Select
                      value={newTicket.priority}
                      onValueChange={(v) => setNewTicket({...newTicket, priority: v})}
                    >
                      <SelectTrigger data-testid="ticket-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgente">Urgente - Ospite bloccato</SelectItem>
                        <SelectItem value="alta">Alta - Ospite disturbato</SelectItem>
                        <SelectItem value="media">Media - Da risolvere presto</SelectItem>
                        <SelectItem value="bassa">Bassa - Quando possibile</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="is_room_occupied"
                    checked={newTicket.is_room_occupied}
                    onChange={(e) => setNewTicket({...newTicket, is_room_occupied: e.target.checked})}
                    className="rounded border-slate-300 w-5 h-5"
                  />
                  <Label htmlFor="is_room_occupied" className="text-amber-800 cursor-pointer">
                    Camera attualmente occupata (aumenta la priorità)
                  </Label>
                </div>

                {/* Firma operatore — obbligatoria per Front Office (account condiviso) */}
                <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                  <Label htmlFor="operator_signature" className="flex items-center gap-1">
                    Firma operatore <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="operator_signature"
                    type="text"
                    value={newTicket.operator_signature}
                    onChange={(e) => setNewTicket({...newTicket, operator_signature: e.target.value})}
                    placeholder="Inserisci il tuo nome (es. Mario Rossi)"
                    required
                    minLength={2}
                    maxLength={80}
                    data-testid="ticket-operator-signature-input"
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Obbligatoria: identifica l&apos;operatore che ha aperto il ticket (account Front Office condiviso).
                  </p>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                    Annulla
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-blue-900 hover:bg-blue-800 gap-2" 
                    disabled={submitting}
                    data-testid="ticket-submit-button"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Invia Ticket
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card" data-testid="stat-total">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Ticket className="w-6 h-6 text-blue-900" />
              </div>
              <div>
                <p className="text-2xl font-black text-slate-900">{data?.total_tickets || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Ticket Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-open">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-amber-600">{data?.open_tickets || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Aperti</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-critical">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-red-600">{data?.critical_tickets || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Critici</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-today">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-green-600">{data?.created_today || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Creati Oggi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My recent tickets */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold">I Miei Ticket Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-80">
              <div className="space-y-3">
                {data?.my_tickets?.map(ticket => (
                  <div
                    key={ticket.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    data-testid={`my-ticket-${ticket.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`priority-${ticket.priority} text-xs`}>
                          {priorityLabels[ticket.priority]}
                        </Badge>
                        <Badge variant="secondary" className={`status-${ticket.status} text-xs`}>
                          {statusLabels[ticket.status]}
                        </Badge>
                      </div>
                      <h4 className="font-medium text-slate-900 truncate">{ticket.title}</h4>
                      <p className="text-sm text-slate-500">
                        {ticket.area} • {formatRomeDate(ticket.created_at)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </div>
                ))}
                
                {(!data?.my_tickets || data.my_tickets.length === 0) && (
                  <div className="text-center py-8">
                    <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Non hai ancora creato ticket</p>
                    <Button 
                      className="mt-4 bg-blue-900 hover:bg-blue-800"
                      onClick={() => setCreateOpen(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Crea il primo ticket
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* All recent tickets */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-bold">Tutti i Ticket Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-80">
              <div className="space-y-3">
                {data?.recent_tickets?.map(ticket => (
                  <div
                    key={ticket.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    data-testid={`recent-ticket-${ticket.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`priority-${ticket.priority} text-xs`}>
                          {priorityLabels[ticket.priority]}
                        </Badge>
                        <Badge variant="secondary" className={`status-${ticket.status} text-xs`}>
                          {statusLabels[ticket.status]}
                        </Badge>
                      </div>
                      <h4 className="font-medium text-slate-900 truncate">{ticket.title}</h4>
                      <p className="text-sm text-slate-500">{ticket.area}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Quick action - big button */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-8 text-center">
          <h3 className="text-xl font-bold text-blue-900 mb-2">Segnalazione Rapida</h3>
          <p className="text-blue-700 mb-6">Hai un problema urgente da segnalare?</p>
          <Button 
            size="lg" 
            className="bg-blue-900 hover:bg-blue-800 h-14 px-8 text-lg"
            onClick={() => setCreateOpen(true)}
            data-testid="quick-create-button"
          >
            <Plus className="w-6 h-6 mr-2" />
            Apri Nuovo Ticket
          </Button>
        </CardContent>
      </Card>

      <LatestPatchNotesCard limit={3} />
    </div>
  );
};

export default FrontofficeDashboard;
